<template>
  <div v-if="propertyEnums.status">
    <div class="w-full flex flex-wrap">
      <div class="flex flex-col w-1/4 pr-2 mb-4 justify-end">
        <label for="address_country" class="text-xs flex flex-row mb-2 uppercase">Country *</label>
        <select id="address_country" v-model="searchParams.address_country" class="bg-white rounded h-10 py-0 px-2 shadow text-sm border text-grey-darkest" @change="countryChange()">
          <option value="">Please Select</option>
          <option v-for="(option, i) in propertyEnums.fc_country" :key="i" :value="option.value">{{ option.label }}</option>
        </select>
      </div>
      <div class="flex flex-col w-1/4 pr-2 mb-4 justify-end">
        <label for="office" class="text-xs flex flex-row mb-2 uppercase">Office *</label>
        <v-select
            multiple
            v-model="searchParams.office"
            placeholder="[All Offices]"
            :options="offices"
            :reduce="office => office.id"
            label="name"
            class="bg-white text-sm shadow text-grey-darkest"
            :class="searchParams.office ? (searchParams.office.length === 0  ? 'h-10' : 'h-auto') : 'h-10'"
        >
        </v-select>
      </div>
      <div class="flex flex-col w-1/4 pr-4 mb-4 justify-end">
        <label for="location" class="text-xs flex flex-row mb-2 uppercase">Postcode / Location</label>
        <locality-suggestion-box v-model="searchParams.location" :country="searchParams.address_country" ref="location_autocomplete" class="text-grey-darkest" />
      </div>
      <div class="flex flex-col w-1/4 pr-6 mb-4 justify-end">
        <label for="radius" class="text-xs flex flex-row mb-2 uppercase">Postcode / Location Radius</label>
        <pl-select
          v-model="searchParams.radius_miles"
          name="radius_miles_menu"
          id="radius_miles_menu"
          select-class="bg-white rounded h-10 py-0 shadow text-sm text-grey-darkest"
          class="w-full"
        >
          <option value="">No Postcode / Location Radius</option>
          <option :key="option.value" v-for="option of distanceOptions" :value="option.value"> {{ option.name }}</option>
        </pl-select>
      </div>

      <div class="flex flex-col w-1/4 pr-4 mb-4 justify-end">
        <label for="department" class="text-xs flex flex-row mb-2 uppercase">Department *</label>
        <pl-select
          v-model="searchParams.department"
          name="department"
          id="department"
          select-class="bg-white rounded h-10 py-0 shadow text-sm text-grey-darkest"
          class="w-full"
        >
          <option value="RS">For Sale</option>
          <option value="RL">To Let</option>
        </pl-select>
      </div>
      <div class="flex flex-col w-1/4 pr-4 mb-4 justify-end">
        <label for="min_price" class="text-xs flex flex-row mb-2 uppercase">Min Price</label>
        <pl-select v-model="searchParams.min_price" id="min_price" name="min_price" select-class="bg-white rounded h-10 py-0 shadow text-sm text-grey-darkest">
          <option :value="0">No Minimum</option>
          <option v-for="n in priceRange" :key="n" :value="n" v-if="searchParams.max_price === 0 || searchParams.max_price > n">
            £{{ n | number }}
          </option>
        </pl-select>
      </div>
      <div class="flex flex-col w-1/4 pr-4 mb-4 justify-end">
        <label for="max_price" class="text-xs flex flex-row mb-2 uppercase">Max Price</label>
        <pl-select v-model="searchParams.max_price" id="max_price" name="max_price" select-class="bg-white rounded h-10 py-0 shadow text-sm text-grey-darkest">
          <option v-for="n in priceRange" :key="n" :value="n" v-if="searchParams.min_price === 0 || searchParams.min_price < n">
            £{{ n | number }}
          </option>
          <option :value="0">No Maximum</option>
        </pl-select>
      </div>
      <div class="flex flex-col w-1/4 pr-4 mb-4 justify-end">
        <label for="type_menu" class="text-xs flex flex-row mb-2 uppercase">Type</label>
        <v-select
            multiple
            v-model="searchParams.type"
            placeholder="[All Types]"
            :options="typeOptions"
            :reduce="type => type.value"
            label="name"
            class="bg-white text-sm text-grey-darkest"
            :class="searchParams.type ? (searchParams.type.length === 0  ? 'h-10' : 'h-auto') : 'h-10'"
        >
        </v-select>
      </div>

      <div class="flex flex-col w-1/4 pr-4 mb-4 justify-end">

        <label for="status" class="text-xs flex flex-row mb-2 uppercase">Status</label>
        <v-select
            multiple
            v-model="searchParams.status"
            placeholder="[All Status]"
            :options="propertyEnums.status[searchParams.department =='RL' ? 'lettings' : 'sales']"
            :reduce="status => status.value"
            label="label"
            class="bg-white text-sm text-grey-darkest"
            :class="searchParams.status ? (searchParams.status.length === 0  ? 'h-10' : 'h-auto') : 'h-10'"
        >
        </v-select>
      </div>

      <div class="flex flex-col w-1/4 pr-4 mb-4 justify-end">
        <label for="min_bedrooms" class="text-xs flex flex-row mb-2 uppercase">Min Bedrooms</label>
        <input id="min_bedrooms" type="number"
               min="0" v-model="searchParams.min_bedrooms" placeholder="Min Bedrooms"
               class="bg-white rounded h-10 py-0 px-2 shadow text-sm border text-grey-darkest number-field"/>
      </div>
      <div class="flex flex-col w-1/4 pr-4 mb-4 justify-end">
        <label for="max_bedrooms" class="text-xs flex flex-row mb-2 uppercase">Max Bedrooms</label>
        <input id="max_bedrooms" type="number"
               min="0" v-model="searchParams.max_bedrooms" placeholder="Max Bedrooms"
               class="bg-white rounded h-10 py-0 px-2 shadow text-sm border text-grey-darkest number-field"/>
      </div>
      <div class="flex flex-col w-1/4 pr-4 mb-4 justify-end">
        <label for="listed_in" class="text-xs flex flex-row mb-2 uppercase">Listed in</label>
        <pl-select
          v-model="searchParams.listed_in"
          name="listed_in"
          id="listed_in"
          select-class="bg-white rounded h-10 py-0 shadow text-sm text-grey-darkest"
          class="w-full"
        >
          <option :key="option.value" v-for="option of dateOptions" :value="option.value"> {{ option.name }}</option>
        </pl-select>
      </div>
      <!-- <div class="flex flex-col w-1/4 pr-4 mb-4 justify-end">
        <label for="min_land_acres" class="text-xs flex flex-row mb-2 uppercase">Min Land(Acres)</label>
        <input id="min_land_acres" type="number"
               min="0" v-model="searchParams.min_land_acres" placeholder="Max Land(Acres)"
               class="bg-white rounded h-10 py-0 px-2 shadow text-sm border"/>
      </div>
      <div class="flex flex-col w-1/4 pr-4 mb-4 justify-end">
        <label for="max_land_acres" class="text-xs flex flex-row mb-2 uppercase">Max Land(Acres)</label>
        <input id="max_land_acres" type="number"
               min="0" v-model="searchParams.max_land_acres" placeholder="Max Land(Acres)"
               class="bg-white rounded h-10 py-0 px-2 shadow text-sm border"/>
      </div>
      <div class="flex flex-col w-1/4 pr-4 mb-4 justify-end">
        <label for="min_square" class="text-xs flex flex-row mb-2 uppercase">Min SQ/FT</label>
        <input id="min_square" type="number"
               min="0" v-model="searchParams.min_square" placeholder="Min SQ/FT"
               class="bg-white rounded h-10 py-0 px-2 shadow text-sm border"/>
      </div>
      <div class="flex flex-col w-1/4 pr-4 mb-4 justify-end">
        <label for="max_square" class="text-xs flex flex-row mb-2 uppercase">Max SQ/FT</label>
        <input id="max_square" type="number"
               min="0" v-model="searchParams.max_square" placeholder="Max SQ/FT"
               class="bg-white rounded h-10 py-0 px-2 shadow text-sm border"/>
      </div> -->
      <div class="flex flex-col pr-4 mb-4 justify-end">
        <div>
          <button type="button"
                  class="bg-tertiary hover:border-tertiary text-white h-10 py-2 px-6 text-sm border border-grey-lightest hover:border-transparent rounded mr-2"
                  @click="search">
            <i class="fas fa-search"></i> Search
          </button>
          <button type="button"
                  class="bg-tertiary hover:border-tertiary text-white h-10 py-2 px-6 text-sm border border-grey-lightest hover:border-transparent rounded mr-2"
                  @click="resetSearch" v-if="!editReportId">
            <i class="fas fa-sync"></i> Reset
          </button>
          <button type="button"
                  class="bg-tertiary hover:border-tertiary h-10 py-2 px-6 text-sm border border-grey-lightest hover:border-transparent rounded mr-2"
                  @click="saving = true"
                  :disabled="selectedProperties.length === 0"
                  :class="selectedProperties.length === 0 ? 'bg-grey text-black tooltip-container' : 'text-white'" v-if="properties.length > 0">
            <i class="fas fa-book"></i> {{this.editReportId ? 'Save Report': 'Create Report'}}
                <div class="absolute border border-bg-primary p-2 bg-white shadow z-10 rounded  normal-case mt-1 tooltip-msg pointer-events-none">
                <span class="text-black font-sans">Please select at least one property to create a report</span>
                </div>
          </button>
          <button type="button"
                  class="bg-tertiary hover:border-tertiary text-white h-10 py-2 px-6 text-sm border border-grey-lightest hover:border-transparent rounded  float-right"
                  @click="backToList()" v-if="this.editReportId"
          >
            <i class="fas fa-home"></i> Back to Report List
          </button>
        </div>
      </div>
    </div>
    <panel v-if="!loading && properties && properties.length > 0" class="mt-5 pt-6">
      <strong class="mb-2">{{totalCount}} properties found from the above search criteria.</strong>
    </panel>
    <panel v-if="selectedProperties.length > 0" class="mt-5 pt-6">
      <strong class="mb-2">{{selectedProperties.length}} properties are selected.</strong>
    </panel>
    <panel class="mt-5 pt-6" v-if="!loading && properties && properties.length > 0">
      <div class="w-full max-w-full overflow-x-auto overflow-y-hidden">
        <table class="table-auto text-center w-full">
          <thead class="uppercase text-xs">
          <tr>
            <th class="text-left p-2 whitespace-no-wrap" colspan="2">
              Property
            </th>
            <th class="p-2 whitespace-no-wrap">
              Type
            </th>
            <th class="p-2 whitespace-no-wrap">
              Listed
            </th>
            <th class="p-2 whitespace-no-wrap">
              Status
            </th>
            <th class="p-2"><span class="cursor-pointer" @click="checkAll">Include <br> <small class="underline">Include All</small></span></th>
            <th class="p-2 whitespace-no-wrap">
              Graded
            </th>
            <th class="p-2 whitespace-no-wrap">
              Land Size
            </th>
            <th class="p-2 whitespace-no-wrap">
              Land Size Unit
            </th>
            <th class="p-2 whitespace-no-wrap">
              Floor size
            </th>
            <th class="p-2 whitespace-no-wrap">
              Floor size units
            </th>
            <th class="p-2 whitespace-no-wrap">
              Price per sq unit
            </th>
            <th class="p-2 whitespace-no-wrap">
              Condition
            </th>
          </tr>
          </thead>
          <tbody class="text-sm">
          <tr v-for="(property, i) in properties" :key="i"
              class="border-t border-grey-lighter hover:bg-grey-lightest cursor-pointer">
            <td>
              <div class="icon-img w-24 h-16 my-2 rounded"
                   :style="{'background-image': `url('${property.main_photo}')`}" v-if="property.main_photo"></div>
              <div v-else class="relative w-24 h-16 px-6 pt-2 my-2 rounded bg-tertiary">
                <icon name="property_cms" class="text-white"/>
              </div>
            </td>
            <td class="text-left p-2 font-bold" @click="getWebsiteLink(property.id)">{{ property.address }}</td>
            <td class="p-2">{{ property.type }}</td>
            <td class="p-2"><div v-if="property.listed_at">
              {{property.price_qualifier}} <br>
              <strong class="mb-2" v-if="property.price_qualifier !== 'POA'">{{ property.currency }}{{ property.price_numeric | number }}</strong> <br v-if="property.price_qualifier !== 'POA'"> {{ property.listed_at | date }}</div></td>
            <td class="p-2">{{ property.availability }}</td>
            <td class="p-2">
              <input type="checkbox" class="green" :id="`include${property.id}`" v-model="selectedProperties" :value="property" />
              <label :for="`include${property.id}`"></label>
            </td>
            <td class="p-2">
              <pl-select
                v-model="property.graded"
                name="graded"
                id="graded"
                select-class="bg-white rounded h-10 py-0 shadow text-sm"
                class="w-full"
                @change="updateProperty(property)"
              >
                <option value="">N/A</option>
                <option value="grade1">Grade I</option>
                <option value="grade2">Grade II*</option>
                <option value="grade3">Grade II</option>
              </pl-select>
            </td>
            <td class="p-2">
              <input id="land_size" type="number" v-model="property.land_size"
              class="bg-white rounded h-10 py-0 px-2 shadow text-sm border"/></td>
            <td class="p-2">
              <select v-model="property.land_size_unit" class="bg-white rounded h-10 py-0 px-2 shadow text-sm border">
                <option v-for="(option, i) in propertyEnums.dimension_unit.large" :key="i" :value="option.value">{{ option.label }}</option>
              </select>
            </td>
            <td class="p-2">
              <input id="floor_size" type="number" v-model="property.floor_size"
              class="bg-white rounded h-10 py-0 px-2 shadow text-sm border" @input="updateProperty(property)"/>
            </td>
            <td class="p-2">
              <select v-model="property.floor_size_unit" class="bg-white rounded h-10 py-0 px-2 shadow text-sm border">
                <option v-for="(option, i) in propertyEnums.dimension_unit.large" :key="i" :value="option.value">{{ option.label }}</option>
              </select>
            </td>
            <td class="p-2">
              <span v-if="property.price_sq">{{ parseFloat(property.price_sq) | gbp }}
              </span>
              <span v-else>-</span>
            </td>
            <td class="p-2">
              <pl-select
                v-model="property.condition"
                name="condition"
                id="condition"
                select-class="bg-white rounded h-10 py-0 shadow text-sm"
                class="w-full"
                @change="updateProperty(property)"
              >
                <option value="Poor">Poor</option>
                <option value="Average">Average</option>
                <option value="Good">Good</option>
                <option value="Excellent">Excellent</option>
                <option value="Outstanding">Outstanding</option>
              </pl-select>
            </td>
          </tr>
          </tbody>
        </table>
        <paginator :data="results.meta" @change="pageChanged" />
      </div>
    </panel>

    <panel v-if="!loading && properties && properties.length === 0" class="mt-5 pt-6">
      <strong class="mb-2">No Properties</strong>
      <p>Your search returned no results</p>
    </panel>
    <panel v-else-if="!loading && properties && properties.length === 0" class="mt-5 pt-6">
      <strong class="mb-2">Press Search button to search properties</strong>
    </panel>
    <div v-if="loading" class="w-full h-screen text-center">
      <loader></loader>
    </div>

    <modal v-if="saving">
      <div v-if="this.editReportId">
        <div class="p-2 mb-2">
          Do you want to update the latest report {{newReport.title}}?
        </div>
        <button @click="updateReport(newReport.id)" class="bg-tertiary border border-tertiary text-white rounded px-4 py-3 text-sm font-medium align-bottom m-2">
          Update Latest Report
        </button>
        <button @click="saving=false" class="bg-grey-lighter border border-tertiary rounded px-4 py-3 text-sm font-medium align-bottom m-2">
          Cancel
        </button>
      </div>
      <div v-else>
        <div class="p-2 mb-2">
          <p class="pr-4 mb-4">Number of Properties Included: {{this.selectedProperties.length}}</p>
          <label for="report_title" class="pr-4">Report Title:</label>
          <input type="text" id="report_title" v-model="newReport.title" class="bg-white rounded h-10 py-0 px-2 mb-2 shadow text-sm border">
          <div class="text-red">{{newReport.error}}</div>
        </div>
        <button @click="saveReport()" class="bg-tertiary border border-tertiary text-white rounded px-4 py-3 text-sm font-medium align-bottom m-2">
          OK
        </button>
        <button @click="saving=false" class="bg-grey-lighter border border-tertiary rounded px-4 py-3 text-sm font-medium align-bottom m-2">
          Cancel
        </button>
      </div>
    </modal>
  </div>
</template>

<script>
import { mapState, mapActions } from 'vuex'
import api, { v2Api } from '@/http'
import debounce from 'lodash/debounce'
import { distanceOptions, dateOptions, propertyStatusOptions, propertyGroupOptions } from '@/selectOptions'
import vSelect from 'vue-select'
import LocalitySuggestionBox from '@/components/LocalitySuggestionBox'
export default {
  name: 'PropertyComparisonReport',
  components: {
    'v-select': vSelect,
    LocalitySuggestionBox
  },
  props: {
    editReportId: {
      type: Number
    }
  },
  data () {
    return {
      searchParams: {
        page: 1,
        department: 'RS'
      },
      results: {},
      loading: false,
      saving: false,
      magazineAlert: null,
      pageHasChanged: false,
      loadingStats: false,
      selectedProperties: [],
      properties: [],
      newReport: {
        id: '',
        title: '',
        error: ''
      },
      distanceOptions: distanceOptions,
      dateOptions: dateOptions,
      propertyStatusOptions: propertyStatusOptions,
      offices: []
    }
  },
  watch: {
    system () {
      this.searchParams.system_id = this.system.id

      if (this.selectedOffice) {
        this.search()
      }
    },
    selectedOffice: {
      handler: function () {
        this.searchParams.office_id = this.selectedOffice.id

        if (this.system) {
          this.search()
        }
      },
      deep: true
    }
  },
  computed: {
    ...mapState(['system', 'selectedOffice', 'propertyEnums']),
    priceRange: function () {
      let output = []
      let i = 0
      switch (this.searchParams.department) {
        case 'RL':
          for (i = 100; i < 500; i += 50) {
            output.push(i)
          }
          for (i = 600; i < 1500; i += 100) {
            output.push(i)
          }
          for (i = 1750; i <= 3000; i += 250) {
            output.push(i)
          }
          for (i = 3500; i <= 7000; i += 500) {
            output.push(i)
          }
          for (i = 8000; i <= 10000; i += 1000) {
            output.push(i)
          }
          for (i = 12500; i <= 20000; i += 2500) {
            output.push(i)
          }
          for (i = 25000; i < 40000; i += 5000) {
            output.push(i)
          }
          return output
        default:
          for (i = 5000; i < 100000; i += 5000) {
            output.push(i)
          }
          for (i = 100000; i < 500000; i += 10000) {
            output.push(i)
          }
          for (i = 500000; i < 1000000; i += 25000) {
            output.push(i)
          }
          for (i = 1000000; i <= 10000000; i += 50000) {
            output.push(i)
          }
          return output
      }
    },
    typeOptions: function () {
      return [...propertyGroupOptions]
    },
    authHeaders () {
      return {
        'Authorization': 'Bearer ' + (localStorage.getItem('access') || localStorage.getItem('accessToken'))
      }
    }
  },
  methods: {
    ...mapActions(['displayToastNotification', 'setPageLoading', 'fetchPropertyEnums']),
    changeOrder (attribute) {
      this.searchParams.page = 1

      if (attribute === this.searchParams.order_attribute) {
        this.searchParams.order_direction = this.searchParams.order_direction === 'desc' ? 'asc' : 'desc'
        return
      }

      this.searchParams.order_attribute = attribute
      this.searchParams.order_direction = 'desc'
    },
    search: debounce(function () {
      this.loading = true
      if (this.pageHasChanged) {
        this.pageHasChanged = false
      } else {
        this.searchParams.page = 1
      }

      api.get('/properties-comparison-report', {
        params: this.searchParams
      }).then((response) => {
        this.results = response.data
        this.properties = response.data.data
        for (const property of this.properties) {
          property.checked = this.selectedProperties.some(selectedProp => selectedProp.id === property.id)
        }
        this.setPageLoading(false)
        this.loading = false
        this.totalCount = response.data.meta.total
      }, (error) => {
        // Error
        this.loading = false
        this.setPageLoading(false)
        let errors = error.response.data.errors
        let errorMessage = error.response.data.message
        if (Object.keys(errors)[0] && errors[Object.keys(errors)[0]]) {
          errorMessage += ' ' + errors[Object.keys(errors)[0]]
        }
        this.displayToastNotification({
          type: 'danger',
          icon: 'fa-times-circle',
          title: 'Error',
          info: errorMessage
        })
      })
    }, 500),
    goToProperty (id) {
      this.$router.push(`/property-management/${id}`)
    },
    pageChanged (i) {
      this.searchParams.page = i
      this.pageHasChanged = true
      this.search()
    },
    checkAll () {
      if (this.selectedProperties.length === this.properties.length) {
        this.selectedProperties = []
        this.properties.map((property, index) => {
          property.checked = false
          return property
        })
      } else {
        this.selectedProperties = this.properties
        this.properties.map((property, index) => {
          property.checked = true
          return property
        })
      }
    },
    getReport (reportId) {
      this.loading = true
      api.get(`/properties-comparison-report/previous-report/${reportId}`).then((response) => {
        this.loading = false
        this.newReport.id = reportId
        this.newReport.title = response.data.report.title
        this.newReport.error = ''
        this.properties = response.data.properties
        this.loading = false
        this.searchParams = {
          system_id: this.system.id,
          office_id: this.selectedOffice.id,
          address_country: response.data.report.address_country ? response.data.report.address_country : 'GB',
          office: response.data.report.offices ? response.data.report.offices : [],
          status: response.data.report.status_ids ? response.data.report.status_ids : [],
          type: response.data.report.property_types ? response.data.report.property_types : [],
          location: response.data.report.keywords ? response.data.report.keywords : '',
          radius_miles: response.data.report.radius_miles ? response.data.report.radius_miles : '',
          department: response.data.report.department ? response.data.report.department : 'RS',
          min_price: response.data.report.min_price ? response.data.report.min_price : 0,
          max_price: response.data.report.max_price ? response.data.report.max_price : 0,
          min_bedrooms: response.data.report.min_bedrooms ? response.data.report.min_bedrooms : null,
          max_bedrooms: response.data.report.max_bedrooms ? response.data.report.max_bedrooms : null,
          listed_in: response.data.report.listed_in ? response.data.report.listed_in : null,
          solded_in: response.data.report.solded_in ? response.data.report.solded_in : null,
          min_land_acres: response.data.report.min_land_acres ? response.data.report.min_land_acres : null,
          max_land_acres: response.data.report.max_land_acres ? response.data.report.max_land_acres : null,
          min_square: response.data.report.min_square ? response.data.report.min_square : null,
          max_square: response.data.report.max_square ? response.data.report.max_square : null,
          page: 1,
          limit: 30
        }
        this.selectedProperties = this.properties
        this.totalCount = this.properties.length
        this.getOfficeList()
      }, () => {
        // Error
        this.loading = false
        this.newReport.error = ''
        this.displayToastNotification({
          type: 'danger',
          icon: 'fa-times-circle',
          title: 'Internal Server Error',
          info: 'Whoops! Looks like something went wrong.'
        })
      })
    },
    saveReport () {
      if (!this.newReport.title) {
        this.newReport.error = 'Please give a title to this report'
        return
      }
      api.post(`/properties-comparison-report/${this.selectedOffice.id}/create`, {
        'critias': this.searchParams,
        'properties': this.selectedProperties,
        'title': this.newReport.title
      }).then((response) => {
        this.saving = false
        if (response.data.result) {
          this.newReport.id = response.data.report_id
          this.newReport.error = ''
          this.displayToastNotification({
            type: 'success',
            icon: 'fa-check',
            title: 'Saved',
            info: 'Property comparison report saved. Go to the Generated Reports tab to view it. '
          })
          this.newReport.title = ''
          // this.$router.replace('/property-comparison-previous-report')
        } else {
          this.saving = false
          this.newReport.error = ''
          this.displayToastNotification({
            type: 'danger',
            icon: 'fa-times-circle',
            title: 'Internal Server Error',
            info: 'Whoops! Looks like something went wrong.' + response.data.message
          })
        }
      }, () => {
        // Error
        this.saving = false
        this.newReport.error = ''
        this.displayToastNotification({
          type: 'danger',
          icon: 'fa-times-circle',
          title: 'Internal Server Error',
          info: 'Whoops! Looks like something went wrong.'
        })
      })
    },
    updateReport (reportId) {
      this.saving = true
      api.post(`/properties-comparison-report/${reportId}/update`, {
        'properties': this.selectedProperties
      }).then((response) => {
        this.saving = false
        if (response.data.result) {
          this.displayToastNotification({
            type: 'success',
            icon: 'fa-check',
            title: 'Saved',
            info: 'Property comparison report saved. Go to the Generated Reports tab to view it'
          })
        } else {
          this.displayToastNotification({
            type: 'danger',
            icon: 'fa-times-circle',
            title: 'Internal Server Error',
            info: 'Whoops! Looks like something went wrong.' + response.data.message
          })
        }
      }, () => {
        // Error
        this.saving = false
        this.displayToastNotification({
          type: 'danger',
          icon: 'fa-times-circle',
          title: 'Internal Server Error',
          info: 'Whoops! Looks like something went wrong.'
        })
      })
    },
    updateProperty: debounce(function (property) {
      if (property.floor_size) {
        property.price_sq = property.price_numeric / property.floor_size
      }
      this.saveProperty(property)
    }, 500),
    saveProperty (property) {
      api.post(`/properties-comparison-report/property/save`, property).then((response) => {
        if (!response.data.result) {
          this.displayToastNotification({
            type: 'danger',
            icon: 'fa-times-circle',
            title: 'Internal Server Error',
            info: 'Whoops! Failed to save the change.' + response.data.message
          })
        }
      }, () => {
        this.displayToastNotification({
          type: 'danger',
          icon: 'fa-times-circle',
          title: 'Internal Server Error',
          info: 'Whoops! Looks like something went wrong.'
        })
      })
    },
    goToPreviousReport () {
      this.$router.replace('/property-comparison-previous-report')
    },
    getWebsiteLink (propertyId) {
      api.get(`/properties-comparison-report/get-website-link/${propertyId}`).then((response) => {
        let url = response.data
        window.open(url, '_blank')
      }, () => {
        this.displayToastNotification({
          type: 'danger',
          icon: 'fa-times-circle',
          title: 'Internal Server Error',
          info: 'Whoops! Looks like something went wrong.'
        })
      })
    },
    loadData () {
      this.fetchPropertyEnums()
    },
    getOfficeList () {
      v2Api.get('office', {
        params: {
          referral_network_id: this.selectedOffice.network_id,
          limit_by_country: this.searchParams.address_country ? (this.searchParams.address_country.length > 1 ? 1 : 0) : 0,
          country: this.searchParams.address_country ? this.searchParams.address_country : 'GB',
          all: 1
        }
      }).then((response) => {
        this.offices = response.data
      })
    },
    addressEndpoint (input) {
      return process.env.VUE_APP_ROOT_API + '/v2/localities/suggest?address_country=GB&q=' + input
    },
    resetSearch () {
      this.searchParams = {
        system_id: this.system.id,
        office_id: this.selectedOffice.id,
        address_country: 'GB',
        radius_miles: '',
        department: 'RS',
        min_price: 0,
        max_price: 0,
        type: null,
        status: null,
        min_bedrooms: null,
        max_bedrooms: null,
        listed_in: null,
        location: '',
        page: 1,
        office: null
      }
      this.$refs.location_autocomplete.reset()
      this.loadData()
      this.getOfficeList()
    },
    backToList () {
      this.$emit('updateComponentVisibility')
    },
    countryChange () {
      this.searchParams.office = null
      this.getOfficeList()
    }
  },
  created () {
    if (this.editReportId) {
      this.getReport(this.editReportId)
    } else {
      this.searchParams = {
        system_id: this.system.id,
        office_id: this.selectedOffice.id,
        address_country: 'GB',
        office: null,
        location: '',
        radius_miles: '',
        department: 'RS',
        min_price: 0,
        max_price: 0,
        status: null,
        min_bedrooms: null,
        max_bedrooms: null,
        type: null,
        listed_in: null,
        solded_in: null,
        min_land_acres: null,
        max_land_acres: null,
        min_square: null,
        max_square: null,
        page: 1,
        limit: 30
      }
    }
    this.loadData()
    this.getOfficeList()
  }
}
</script>
<style>
  .icon-img {
    background-size: cover;
    background-position: center;
  }

  .icon {
    width: 80px;
    height: 60px;
  }
  .vs__dropdown-toggle {
    border: none !important;
  }
  .number-field::placeholder {
    color: #3d4852;
    opacity: 1;
  }
  .vs__search {
    color: #000000 !important;
  }
</style>
